// Testläget (designen Admin-Testlage, ADR 0065): byrån skriver en förfrågan
// som en kund skulle kunna skicka och ser exakt vilken offert den ger, mot
// utkastet eller den publicerade versionen. Inget sparas och inget skickas.
import { useMutation, useQuery } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { data } from '../api/client.js';
import { usePrismodeller } from '../api/queries.js';
import { useAuth } from '../auth/auth.js';
import { BOLAGSFORMER, type LegalForm } from '../katalog/texter.js';
import { Sida } from '../layout/Sida.js';
import { beloppText, orsakText, type Berakning, type Testresultat } from '../testlage/texter.js';

type Hantering = 'digital' | 'paper' | 'unknown';

interface Form {
  legalForm: LegalForm;
  business: string;
  employees: string;
  /** Ägare som är personer, för AB, HB och KB som i widgeten. */
  owners: string;
  invoicesOut: string;
  invoicesIn: string;
  invoiceHandling: Hantering;
  /** Ungefärlig omsättning per år, som i widgeten. */
  turnover: string;
  freeText: string;
}

const TOMT: Form = {
  legalForm: 'AB',
  business: '',
  employees: '0',
  owners: '1',
  invoicesOut: '',
  invoicesIn: '',
  invoiceHandling: 'digital',
  turnover: 'vet ej',
  freeText: '',
};

/** Lindqvist Snickeri från designen. */
export const EXEMPEL: Form = {
  legalForm: 'AB',
  business: 'Snickeri',
  employees: '3',
  owners: '3',
  invoicesOut: '25',
  invoicesIn: 'Vet inte',
  invoiceHandling: 'digital',
  turnover: '3–10 mnkr',
  freeText:
    'Snickeri, mest renoveringar åt privatpersoner. Vi är tre anställda och fakturerar via Fortnox.',
};

/** Samma val som i widgeten (apps/widget/src/form/state.ts). */
const OMSATTNING: { kod: string; namn: string }[] = [
  { kod: 'under 1 mnkr', namn: 'Under 1 miljon kr' },
  { kod: '1–3 mnkr', namn: '1–3 miljoner kr' },
  { kod: '3–10 mnkr', namn: '3–10 miljoner kr' },
  { kod: '10–40 mnkr', namn: '10–40 miljoner kr' },
  { kod: 'över 40 mnkr', namn: 'Över 40 miljoner kr' },
  { kod: 'vet ej', namn: 'Vet inte' },
];

const HANTERING: { kod: Hantering; namn: string }[] = [
  { kod: 'digital', namn: 'Digitalt system' },
  { kod: 'paper', namn: 'Papper eller pdf' },
  { kod: 'unknown', namn: 'Vet inte' },
];

/** Ett antal, eller "vet ej" som i widgeten när fältet är tomt eller inte är ett tal. */
const antal = (text: string): number | 'vet ej' => {
  const t = text.replace(/\s/g, '');
  return /^\d+$/.test(t) ? Number(t) : 'vet ej';
};

/** Bolagsformer där widgeten frågar efter antal ägare. */
const fragarOmAgare = (legalForm: LegalForm) =>
  legalForm === 'AB' || legalForm === 'HB' || legalForm === 'KB';

/** Svaren som widgeten skickar dem (apps/widget/src/form/state.ts). */
export function svarFran(f: Form) {
  const anstallda = Number(f.employees.replace(/\s/g, ''));
  const agare = Number(f.owners.replace(/\s/g, ''));
  return {
    legalForm: f.legalForm,
    ...(f.business.trim() ? { business: f.business.trim() } : {}),
    invoicesOut: antal(f.invoicesOut),
    invoicesIn: antal(f.invoicesIn),
    invoiceHandling: f.invoiceHandling,
    turnover: f.turnover,
    employees: Number.isInteger(anstallda) && anstallda >= 0 ? anstallda : 0,
    ...(fragarOmAgare(f.legalForm)
      ? { owners: Number.isInteger(agare) && agare >= 1 ? agare : 1 }
      : {}),
  };
}

export function Testlage() {
  const { api } = useAuth();
  const modeller = usePrismodeller();
  const [f, setF] = useState<Form>(TOMT);
  const [valdId, setValdId] = useState<string | null>(null);
  const id = useId();

  const lista = (modeller.data ?? []).filter((m) => m.status !== 'archived');
  const version = lista.find((m) => m.id === valdId) ?? lista[0];

  const katalog = useQuery({
    queryKey: ['prismodell', version?.id],
    queryFn: async () =>
      data(
        await api.GET('/v1/admin/pricing-models/{id}', {
          params: { path: { id: version?.id ?? '' } },
        }),
      ),
    enabled: version !== undefined,
  });

  const testa = useMutation({
    mutationFn: async (form: Form) => {
      if (!version) throw new Error('Ingen version att testa mot');
      return data(
        await api.POST('/v1/admin/pricing-models/{id}/test', {
          params: { path: { id: version.id } },
          body: {
            answers: svarFran(form),
            ...(form.freeText.trim() ? { freeText: form.freeText.trim() } : {}),
          },
        }),
      );
    },
  });

  function rakna(e: FormEvent) {
    e.preventDefault();
    testa.mutate(f);
  }

  const satt = (nytt: Partial<Form>) => setF((g) => ({ ...g, ...nytt }));

  if (modeller.isSuccess && lista.length === 0) {
    return (
      <Sida titel="Testläge">
        <div className="kort kom-igang-kort">
          <h2>Ni har ingen tjänstekatalog att testa än</h2>
          <p>Börja med en startmall eller en tom katalog.</p>
          <Link className="btn btn-p" to="/kom-igang">
            Välj startmall
          </Link>
        </div>
      </Sida>
    );
  }

  return (
    <Sida titel="Testläge">
      <p className="ingress katalog-huvud">
        Skriv en förfrågan som en kund skulle kunna skicka och se exakt vilken offert den ger. Inget
        skickas.
      </p>
      <div className="testlage">
        <form className="kort flikkort" onSubmit={rakna}>
          <div className="falt">
            <label className="etikett" htmlFor={`${id}-version`}>
              Testa mot
            </label>
            <select
              id={`${id}-version`}
              className="in"
              value={version?.id ?? ''}
              onChange={(e) => setValdId(e.target.value)}
            >
              {lista.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.status === 'draft'
                    ? 'Utkast (ej publicerat)'
                    : `Version ${m.version} – publicerad`}
                </option>
              ))}
            </select>
          </div>
          <div className="tva">
            <div className="falt">
              <label className="etikett" htmlFor={`${id}-bf`}>
                Bolagsform
              </label>
              <select
                id={`${id}-bf`}
                className="in"
                value={f.legalForm}
                onChange={(e) => satt({ legalForm: e.target.value as LegalForm })}
              >
                {BOLAGSFORMER.map((b) => (
                  <option key={b.kod} value={b.kod}>
                    {b.namn}
                  </option>
                ))}
              </select>
            </div>
            <div className="falt">
              <label className="etikett" htmlFor={`${id}-anst`}>
                Anställda med lön
              </label>
              <input
                id={`${id}-anst`}
                className="in mono"
                inputMode="numeric"
                value={f.employees}
                onChange={(e) => satt({ employees: e.target.value })}
              />
            </div>
            {fragarOmAgare(f.legalForm) && (
              <div className="falt">
                <label className="etikett" htmlFor={`${id}-agare`}>
                  Ägare (individer)
                </label>
                <input
                  id={`${id}-agare`}
                  className="in mono"
                  inputMode="numeric"
                  value={f.owners}
                  onChange={(e) => satt({ owners: e.target.value })}
                />
              </div>
            )}
            <div className="falt">
              <label className="etikett" htmlFor={`${id}-ut`}>
                Kundfakturor/mån
              </label>
              <input
                id={`${id}-ut`}
                className="in mono"
                value={f.invoicesOut}
                placeholder="Vet inte"
                onChange={(e) => satt({ invoicesOut: e.target.value })}
              />
            </div>
            <div className="falt">
              <label className="etikett" htmlFor={`${id}-in`}>
                Leverantörsfakturor/mån
              </label>
              <input
                id={`${id}-in`}
                className="in mono"
                value={f.invoicesIn}
                placeholder="Vet inte"
                onChange={(e) => satt({ invoicesIn: e.target.value })}
              />
            </div>
          </div>
          <div className="falt">
            <label className="etikett" htmlFor={`${id}-verk`}>
              Verksamhet
            </label>
            <input
              id={`${id}-verk`}
              className="in"
              value={f.business}
              placeholder="Till exempel snickeri"
              onChange={(e) => satt({ business: e.target.value })}
            />
          </div>
          <div className="falt">
            <label className="etikett" htmlFor={`${id}-oms`}>
              Omsättning per år
            </label>
            <select
              id={`${id}-oms`}
              className="in"
              value={f.turnover}
              onChange={(e) => satt({ turnover: e.target.value })}
            >
              {OMSATTNING.map((o) => (
                <option key={o.kod} value={o.kod}>
                  {o.namn}
                </option>
              ))}
            </select>
          </div>
          <div className="falt">
            <label className="etikett" htmlFor={`${id}-hant`}>
              Hantering
            </label>
            <select
              id={`${id}-hant`}
              className="in"
              value={f.invoiceHandling}
              onChange={(e) => satt({ invoiceHandling: e.target.value as Hantering })}
            >
              {HANTERING.map((h) => (
                <option key={h.kod} value={h.kod}>
                  {h.namn}
                </option>
              ))}
            </select>
          </div>
          <div className="falt">
            <label className="etikett" htmlFor={`${id}-txt`}>
              Kundens fritext
            </label>
            <textarea
              id={`${id}-txt`}
              className="in"
              rows={5}
              value={f.freeText}
              onChange={(e) => satt({ freeText: e.target.value })}
            />
          </div>
          <div className="panel-fot">
            <button type="button" className="btn btn-s" onClick={() => setF(EXEMPEL)}>
              Fyll i ett exempel
            </button>
            <button type="submit" className="btn btn-p" disabled={testa.isPending || !version}>
              {testa.isPending ? 'Räknar …' : 'Räkna fram offerten'}
            </button>
          </div>
        </form>
        <section
          className="kort flikkort resultat"
          aria-labelledby={`${id}-res`}
          aria-busy={testa.isPending}
        >
          <h2 id={`${id}-res`} className="resultat-rubrik">
            Resultat
          </h2>
          <div aria-live="polite">
            {testa.isPending && <p className="hjalp">AI tolkar förfrågan …</p>}
            {testa.isError && (
              <div className="meddelande" role="alert">
                Det gick inte att testa förfrågan. Försök igen om en stund.
              </div>
            )}
            {testa.isSuccess && (
              <Resultat resultat={testa.data} katalog={katalog.data?.definition} />
            )}
            {testa.isIdle && (
              <p className="hjalp">Fyll i förfrågan och välj Räkna fram offerten.</p>
            )}
          </div>
        </section>
      </div>
    </Sida>
  );
}

function Resultat({
  resultat,
  katalog,
}: {
  resultat: Testresultat;
  katalog: Parameters<typeof orsakText>[1];
}) {
  const t = resultat.interpretation;
  const skickas = resultat.outcome === 'dispatch';
  return (
    <div className="resultat-innehall">
      <div className="resultat-status">
        <span className={`chip ${skickas ? 'chip-gron' : 'chip-gul'}`}>
          {skickas ? 'Skulle skickas automatiskt' : 'Skulle granskas av er först'}
        </span>
        <span className="hjalp">
          {t ? `Säkerhet ${t.confidencePercent} % · ` : ''}
          {(resultat.latencyMs / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} s
        </span>
      </div>
      {resultat.causes.length > 0 && (
        <div className="ruta ruta-gul">
          <h3>Varför den skulle granskas</h3>
          <ul>
            {resultat.causes.map((o, i) => (
              <li key={i}>{orsakText(o, katalog)}</li>
            ))}
          </ul>
        </div>
      )}
      {t && t.assumptions.length > 0 && (
        <div className="ruta">
          <h3>AI har utgått från</h3>
          <ul>
            <li>Verksamhet: {t.businessType}</li>
            {t.assumptions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}
      {resultat.calculation && <Rader berakning={resultat.calculation} />}
      {t?.introText && (
        <div>
          <h3 className="liten-rubrik">Inledningen i offerten</h3>
          <p className="intro">{t.introText}</p>
        </div>
      )}
      {resultat.calculation && (
        <p className="hjalp">
          AI-justering: {resultat.calculation.adjustment?.appliedPercent ?? 0} %.{' '}
          {t && t.flags.length > 0
            ? `Flaggor: ${t.flags.map((fl) => fl.code).join(', ')}.`
            : 'Inga flaggor.'}
        </p>
      )}
    </div>
  );
}

function Rader({ berakning }: { berakning: Berakning }) {
  const period = (b: string) => berakning.periods.find((p) => p.billing === b);
  return (
    <>
      <div>
        <h3 className="liten-rubrik">Valda tjänster och pris exkl. moms</h3>
        <ul className="rader">
          {berakning.periods.flatMap((p) =>
            p.lines.map((l) => (
              <li key={`${p.billing}-${l.serviceId}`} className="rad">
                <span className="rad-namn">
                  {l.label}
                  {l.units ? ` × ${l.units.quantity}` : ''}
                </span>
                <span className="mono">{beloppText(l.amount, l.billing)}</span>
                <span className="rad-varfor">{l.reason}</span>
              </li>
            )),
          )}
          {berakning.optionalLines.map((l) => (
            <li key={`tillval-${l.serviceId}`} className="rad tillval">
              <span>Tillval: {l.label}</span>
              <span className="mono">{beloppText(l.amount, l.billing)}</span>
              <span className="rad-varfor">{l.reason}</span>
            </li>
          ))}
        </ul>
      </div>
      <dl className="summor">
        {(['monthly', 'yearly', 'once'] as const).map((b) => {
          const p = period(b);
          return (
            <div key={b}>
              <dt className="hjalp">
                {{ monthly: 'Per månad', yearly: 'Per år', once: 'En gång' }[b]}
              </dt>
              <dd className="mono">{p ? beloppText(p.total) : '–'}</dd>
            </div>
          );
        })}
      </dl>
    </>
  );
}
